/**
 * Lap-time and track-time parsing shared by the grid, the pit board and the
 * strategy analytics.
 *
 * Previously each of these lived as a private copy in `TeamInfo`,
 * `usePitAssignments` and `TeamStintsModal`, which is why they drifted.
 * (`backend/src/apex/team-info.ts` keeps its own copy — the two packages share
 * no module, so consolidating across the wire would need a shared package.)
 */

/**
 * Parse a lap time (`"M:SS.mmm"` or `"SS.mmm"`) into seconds.
 * Returns null for placeholders like `""`, `"-"` and `"--:--.---"`.
 */
export function parseLapTimeSeconds(lapTime: string): number | null {
  if (!lapTime) return null;

  const match = lapTime.match(/^(?:(\d+):)?(\d+\.\d+)$/);
  if (!match) return null;

  const minutes = match[1] ? parseInt(match[1], 10) : 0;
  return minutes * 60 + parseFloat(match[2]);
}

/** Format a duration in seconds as `M:SS.mmm`, or `SS.mmm` under a minute. */
export function formatLapSeconds(totalSeconds: number): string {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = (totalSeconds % 60).toFixed(3);
  return minutes > 0 ? `${minutes}:${seconds.padStart(6, '0')}` : seconds;
}

/**
 * Apex sends two different clocks down the same `otr` cell, distinguished only
 * by a trailing dot:
 *
 *   `"1:05"`  — on track, hours:minutes, ticking once a minute (stint age)
 *   `"47."` / `"2:16."` — in the pit, seconds (rolling into M:SS at 60s)
 *
 * Reading the dotted form as `H:MM` is the trap here: `"2:16."` is a kart that
 * has been stationary for 2m16s, not one 136 minutes into a stint.
 */
export type TrackTimeReading =
  | { kind: 'onTrack'; minutes: number }
  | { kind: 'inPit'; seconds: number };

export function parseTrackTime(raw: string): TrackTimeReading | null {
  const value = raw.trim();
  if (!value) return null;

  // In-pit stopwatch: "SS." or "M:SS.".
  const pit = value.match(/^(?:(\d+):)?(\d{1,2})\.$/);
  if (pit) {
    const minutes = pit[1] ? parseInt(pit[1], 10) : 0;
    return { kind: 'inPit', seconds: minutes * 60 + parseInt(pit[2], 10) };
  }

  // On-track stint age: "H:MM".
  const track = value.match(/^(\d+):(\d{1,2})$/);
  if (track) {
    return {
      kind: 'onTrack',
      minutes: parseInt(track[1], 10) * 60 + parseInt(track[2], 10),
    };
  }

  return null;
}

/** Stint age in minutes, or null when the kart is boxed or the value is unparseable. */
export function stintMinutes(onTrackTime: string): number | null {
  const reading = parseTrackTime(onTrackTime);
  return reading?.kind === 'onTrack' ? reading.minutes : null;
}

/** Apex's own pit stopwatch in seconds, or null when the kart is on track. */
export function pitElapsedSeconds(onTrackTime: string): number | null {
  const reading = parseTrackTime(onTrackTime);
  return reading?.kind === 'inPit' ? reading.seconds : null;
}
